import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Check, X, Plus, Send, AtSign, Trash2, Lock, Unlock, Eye, User,
  Hammer, Briefcase, Building2, ShieldCheck,
} from 'lucide-react';

/**
 * Receipt Annotation Preview — FINAL.
 * Locks in:
 *   - Color-coded dots (blue/amber/green/purple) with sticky-note popups
 *   - "Internal only" padlock toggle per note (hidden from investor + contractor)
 *   - Investor role upgraded to read+annotate (banker access path)
 *   - Two-way annotation flow with contractors on their own files
 *   - Viewer-perspective switcher at top so the user can see what each role sees
 */

const COLORS = {
  blue:   { dot: '#3b82f6', halo: 'rgba(59,130,246,0.35)',  bg: '#dbeafe', border: '#93c5fd', text: '#1e3a8a', label: 'Question' },
  amber:  { dot: '#f59e0b', halo: 'rgba(245,158,11,0.35)',  bg: '#fef3c7', border: '#fcd34d', text: '#78350f', label: 'Flag' },
  green:  { dot: '#10b981', halo: 'rgba(16,185,129,0.35)',  bg: '#d1fae5', border: '#6ee7b7', text: '#064e3b', label: 'Approved' },
  purple: { dot: '#8b5cf6', halo: 'rgba(139,92,246,0.35)',  bg: '#ede9fe', border: '#c4b5fd', text: '#4c1d95', label: 'Info' },
};

const ROLES = {
  owner:      { label: 'You (Owner)',           icon: User,        tone: 'bg-blue-100 text-blue-800 border-blue-300',         desc: 'Sees every note. Can add, reply, resolve, delete. Can mark notes internal-only.' },
  accountant: { label: 'Accountant / Finance',  icon: Briefcase,   tone: 'bg-emerald-100 text-emerald-800 border-emerald-300',desc: 'Sees every note (incl. internal). Can add, reply, resolve. Can delete own notes only.' },
  investor:   { label: 'Banker (Investor)',     icon: Building2,   tone: 'bg-purple-100 text-purple-800 border-purple-300',   desc: 'Sees external-visible notes only — internal notes are hidden. Can add + reply, can\'t delete or unlock.' },
  contractor: { label: 'Contractor',            icon: Hammer,      tone: 'bg-amber-100 text-amber-800 border-amber-300',      desc: 'Only sees notes on files they uploaded OR where they\'re @mentioned. Internal-only notes always hidden.' },
};

// SAMPLE INVOICE ----------------------------------------------------

const SampleInvoice = ({ children }) => (
  <div
    style={{
      background: '#fff', borderRadius: 10, padding: '32px 36px', minHeight: 560,
      boxShadow: '0 18px 50px -20px rgba(15,23,42,0.35)', position: 'relative',
      fontFamily: '"Times New Roman", serif',
    }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #0f172a', paddingBottom: 16 }}>
      <div>
        <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a' }}>BLOCK 7 CONTRACTING, LLC</div>
        <div style={{ fontSize: 12, color: '#475569', marginTop: 4, lineHeight: 1.5 }}>
          412 Industrial Park Rd, Suite B<br />
          Baltimore, MD 21227<br />
          (410) 555-2147 · hello@block7.com
        </div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: 26, fontWeight: 700, color: '#0f172a', letterSpacing: 1 }}>INVOICE</div>
        <div style={{ fontSize: 12, color: '#475569', marginTop: 6 }}>#INV-2026-0419</div>
        <div style={{ fontSize: 12, color: '#475569' }}>04/15/2026</div>
      </div>
    </div>

    <div style={{ marginTop: 20, fontSize: 12, color: '#1e293b' }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#94a3b8', letterSpacing: 1 }}>Bill To</div>
      <div style={{ fontWeight: 600, marginTop: 4 }}>Mantua Pikesville LLC</div>
      <div style={{ color: '#475569' }}>1615 38th St · Baltimore, MD</div>
      <div style={{ color: '#475569' }}>Phase: Framing Labor</div>
    </div>

    <table style={{ width: '100%', marginTop: 24, fontSize: 13, borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ borderBottom: '1px solid #e2e8f0', color: '#475569' }}>
          <th style={{ textAlign: 'left', padding: '8px 0' }}>Description</th>
          <th style={{ textAlign: 'right', padding: '8px 0' }}>Qty</th>
          <th style={{ textAlign: 'right', padding: '8px 0' }}>Rate</th>
          <th style={{ textAlign: 'right', padding: '8px 0' }}>Amount</th>
        </tr>
      </thead>
      <tbody style={{ color: '#0f172a' }}>
        <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
          <td style={{ padding: '12px 0' }}>Framing labor — first floor, full crew</td>
          <td style={{ textAlign: 'right' }}>5 days</td>
          <td style={{ textAlign: 'right' }}>$1,850</td>
          <td style={{ textAlign: 'right', fontWeight: 600 }}>$9,250.00</td>
        </tr>
        <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
          <td style={{ padding: '12px 0' }}>Framing labor — second floor + roof deck</td>
          <td style={{ textAlign: 'right' }}>6 days</td>
          <td style={{ textAlign: 'right' }}>$1,850</td>
          <td style={{ textAlign: 'right', fontWeight: 600 }}>$11,100.00</td>
        </tr>
        <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
          <td style={{ padding: '12px 0' }}>Materials — lumber package (per attached PO)</td>
          <td style={{ textAlign: 'right' }}>1</td>
          <td style={{ textAlign: 'right' }}>$14,260</td>
          <td style={{ textAlign: 'right', fontWeight: 600 }}>$14,260.00</td>
        </tr>
        <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
          <td style={{ padding: '12px 0' }}>Equipment rental — scaffolding, week 2-3</td>
          <td style={{ textAlign: 'right' }}>14 days</td>
          <td style={{ textAlign: 'right' }}>$85</td>
          <td style={{ textAlign: 'right', fontWeight: 600 }}>$1,190.00</td>
        </tr>
      </tbody>
      <tfoot>
        <tr style={{ color: '#0f172a' }}>
          <td colSpan={3} style={{ textAlign: 'right', padding: '12px 16px 4px', fontSize: 13 }}>Subtotal</td>
          <td style={{ textAlign: 'right', padding: '12px 0 4px' }}>$35,800.00</td>
        </tr>
        <tr style={{ borderTop: '2px solid #0f172a', color: '#0f172a' }}>
          <td colSpan={3} style={{ textAlign: 'right', padding: '12px 16px 4px', fontSize: 15, fontWeight: 700 }}>Total Due</td>
          <td style={{ textAlign: 'right', padding: '12px 0 4px', fontSize: 15, fontWeight: 700 }}>$35,800.00</td>
        </tr>
      </tfoot>
    </table>

    <div style={{ marginTop: 28, fontSize: 11, color: '#64748b', borderTop: '1px dashed #e2e8f0', paddingTop: 16 }}>
      Net 15 · Payable to Block 7 Contracting LLC · ACH details on file
    </div>

    {children}
  </div>
);

// DOT --------------------------------------------------------------

const Dot = ({ note, active, onClick }) => {
  const c = COLORS[note.color] || COLORS.blue;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      title={note.preview}
      style={{
        position: 'absolute', left: note.x, top: note.y,
        width: 18, height: 18, borderRadius: '50%',
        background: c.dot,
        border: '3px solid #fff',
        boxShadow: `0 0 0 ${active ? '6px' : '0px'} ${c.halo}, 0 6px 14px -2px rgba(15,23,42,0.25)`,
        transition: 'box-shadow 140ms ease, transform 140ms ease',
        transform: active ? 'scale(1.15)' : 'scale(1)',
        animation: active ? 'none' : 'pulse 2.4s ease-in-out infinite',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      {note.internalOnly && (
        <span style={{ position: 'absolute', right: -6, bottom: -6, background: '#fff', borderRadius: '50%', padding: 1.5, boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
          <Lock className="w-2.5 h-2.5" style={{ color: c.dot }} />
        </span>
      )}
    </button>
  );
};

// STICKY NOTE -------------------------------------------------------

const StickyNote = ({ note, role, onClose, onResolve, onDelete, onReply, onToggleInternal }) => {
  const c = COLORS[note.color] || COLORS.blue;
  const [draft, setDraft] = useState('');
  const rightSide = note.x < 420;

  const canDelete = (role === 'owner') || (note.author === ROLES[role].label && role !== 'investor');
  const canToggleInternal = role === 'owner' || role === 'accountant';
  const canResolve = role !== 'investor' && role !== 'contractor' ? true : (note.author === ROLES[role].label);

  return (
    <div
      style={{ position: 'absolute', left: rightSide ? note.x + 32 : note.x - 312, top: Math.max(8, note.y - 18), width: 290, zIndex: 5 }}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{ position: 'absolute', left: rightSide ? -10 : 'auto', right: rightSide ? 'auto' : -10, top: 24, width: 0, height: 0, borderTop: '8px solid transparent', borderBottom: '8px solid transparent', [rightSide ? 'borderRight' : 'borderLeft']: `10px solid ${c.bg}` }} />
      <div style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 10, padding: 12, boxShadow: '0 14px 32px -8px rgba(15,23,42,0.35)', color: c.text }}>
        <div className="flex items-center gap-2 mb-2">
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: c.dot, boxShadow: `0 0 0 3px ${c.halo}` }} />
          <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{c.label}</span>
          {note.internalOnly && (
            <span className="text-[10px] inline-flex items-center gap-1 bg-white/70 px-1.5 py-0.5 rounded font-semibold">
              <Lock className="w-3 h-3" /> Internal only
            </span>
          )}
          {note.resolved && (
            <span className="text-[10px] inline-flex items-center gap-1 bg-white/60 px-1.5 py-0.5 rounded">
              <Check className="w-3 h-3" /> Resolved
            </span>
          )}
          <button onClick={onClose} style={{ marginLeft: 'auto', color: c.text, opacity: 0.7 }}>
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        <div style={{ fontSize: 13, lineHeight: 1.45, color: c.text, fontWeight: 500 }}>{note.body}</div>

        <div style={{ fontSize: 11, marginTop: 6, opacity: 0.75, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: '50%', background: '#fff', color: c.text, fontWeight: 700, fontSize: 10 }}>{(note.author || 'Y')[0].toUpperCase()}</span>
          <span>{note.author} · {note.time}</span>
        </div>

        {(note.replies || []).length > 0 && (
          <div className="mt-2 pl-2" style={{ borderLeft: `2px solid ${c.border}` }}>
            {(note.replies || []).map((r, i) => (
              <div key={i} className="mt-1.5" style={{ fontSize: 12, color: c.text }}>
                <div style={{ fontSize: 10, opacity: 0.75, marginBottom: 1 }}>{r.author} · {r.time}</div>
                <div>{r.text}</div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-2 bg-white/70 rounded-lg flex items-center gap-1 px-2 py-1" style={{ border: `1px solid ${c.border}` }}>
          <AtSign className="w-3 h-3" style={{ color: c.text, opacity: 0.5 }} />
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && draft.trim()) { onReply?.(draft.trim()); setDraft(''); } }}
            placeholder={`Reply as ${ROLES[role].label}…`}
            className="flex-1 bg-transparent outline-none text-xs"
            style={{ color: c.text }}
          />
          <button onClick={() => { if (draft.trim()) { onReply?.(draft.trim()); setDraft(''); } }} style={{ color: c.dot }}>
            <Send className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="flex items-center justify-between mt-2 pt-2 gap-1.5 flex-wrap" style={{ borderTop: `1px dashed ${c.border}` }}>
          <div className="flex items-center gap-2">
            {canResolve && (
              <button onClick={onResolve} className="text-[11px] inline-flex items-center gap-1 hover:underline" style={{ color: c.text }}>
                <Check className="w-3 h-3" /> {note.resolved ? 'Reopen' : 'Resolve'}
              </button>
            )}
            {canToggleInternal && (
              <button onClick={onToggleInternal} className="text-[11px] inline-flex items-center gap-1 hover:underline" style={{ color: c.text }}>
                {note.internalOnly ? <><Unlock className="w-3 h-3" /> Make external</> : <><Lock className="w-3 h-3" /> Internal only</>}
              </button>
            )}
          </div>
          {canDelete && (
            <button onClick={onDelete} className="text-[11px] inline-flex items-center gap-1 hover:underline opacity-80" style={{ color: c.text }}>
              <Trash2 className="w-3 h-3" /> Delete
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// LEGEND ------------------------------------------------------------

const Legend = () => (
  <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm inline-flex items-center gap-4 text-xs flex-wrap">
    {Object.entries(COLORS).map(([k, c]) => (
      <div key={k} className="inline-flex items-center gap-1.5">
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: c.dot, display: 'inline-block', boxShadow: `0 0 0 3px ${c.halo}` }} />
        <span className="text-slate-700 font-medium">{c.label}</span>
      </div>
    ))}
    <span className="text-slate-300">|</span>
    <span className="text-slate-700 inline-flex items-center gap-1"><Lock className="w-3 h-3 text-slate-500" /> Internal-only badge</span>
  </div>
);

// SEED NOTES --------------------------------------------------------

const INITIAL_NOTES = [
  {
    id: 'n1', x: 472, y: 305, color: 'blue', internalOnly: false,
    body: 'Is this the same lumber package as the March PO? Want to make sure we\'re not double-paying.',
    preview: 'Is this the same lumber package as the March PO?',
    author: 'You (Owner)', authorRole: 'owner', time: '2h ago',
    replies: [
      { author: 'Block 7 · Contractor', time: '1h ago', text: 'Different package — second floor only. March PO was first floor.' },
    ],
    mentions: ['contractor'],
    resolved: false,
  },
  {
    id: 'n2', x: 472, y: 245, color: 'amber', internalOnly: true,
    body: '@Sarah pls double-check the day count vs the daily reports BEFORE we share with the bank.',
    preview: 'Internal flag · double-check day count',
    author: 'You (Owner)', authorRole: 'owner', time: '40m ago',
    replies: [],
    mentions: ['accountant'],
    resolved: false,
  },
  {
    id: 'n3', x: 472, y: 358, color: 'green', internalOnly: false,
    body: 'Equipment rental approved — Mark gave verbal OK on the 12th.',
    preview: 'Approved · equipment rental',
    author: 'You (Owner)', authorRole: 'owner', time: 'Yesterday',
    replies: [],
    mentions: [],
    resolved: true,
  },
  {
    id: 'n4', x: 270, y: 195, color: 'purple', internalOnly: false,
    body: 'Phase budget: $36,500. This invoice covers ~98% of the labor allowance.',
    preview: 'Info · phase budget reference',
    author: 'Accountant / Finance', authorRole: 'accountant', time: '3h ago',
    replies: [],
    mentions: [],
    resolved: false,
  },
  {
    id: 'n5', x: 670, y: 305, color: 'amber', internalOnly: false,
    body: 'Can you split out the lumber package by floor in a revised invoice? Easier for the bank reviewer.',
    preview: 'Banker flag · split lumber by floor',
    author: 'Banker (Investor)', authorRole: 'investor', time: '20m ago',
    replies: [],
    mentions: ['owner', 'contractor'],
    resolved: false,
  },
];

// VISIBILITY RULES --------------------------------------------------

function noteVisibleTo(note, role) {
  // Internal-only notes are hidden from investor + contractor
  if (note.internalOnly && (role === 'investor' || role === 'contractor')) return false;
  // Contractors only see notes on their own files OR where they're @mentioned
  // (in this mockup we treat the invoice as theirs, so they see all non-internal notes that mention them or are on this file)
  if (role === 'contractor') {
    return !note.internalOnly && (note.mentions.includes('contractor') || true /* their own file */);
  }
  return true;
}

// PAGE --------------------------------------------------------------

export default function ReceiptAnnotationPreview() {
  const [notes, setNotes] = useState(INITIAL_NOTES);
  const [activeId, setActiveId] = useState('n1');
  const [pickColor, setPickColor] = useState('blue');
  const [viewerRole, setViewerRole] = useState('owner');

  const visibleNotes = useMemo(() => notes.filter(n => noteVisibleTo(n, viewerRole)), [notes, viewerRole]);
  const active = visibleNotes.find(n => n.id === activeId);

  const handleReply = (text) => {
    setNotes(prev => prev.map(n =>
      n.id === activeId
        ? { ...n, replies: [...n.replies, { author: ROLES[viewerRole].label, time: 'just now', text }] }
        : n
    ));
  };
  const handleResolve = () => {
    setNotes(prev => prev.map(n => n.id === activeId ? { ...n, resolved: !n.resolved, color: !n.resolved ? 'green' : n.color } : n));
  };
  const handleToggleInternal = () => {
    setNotes(prev => prev.map(n => n.id === activeId ? { ...n, internalOnly: !n.internalOnly } : n));
  };
  const handleDelete = () => {
    setNotes(prev => prev.filter(n => n.id !== activeId));
    setActiveId(null);
  };
  const handleAddDot = (e) => {
    if (viewerRole === 'investor' && e.target.closest('[data-no-add]')) return;
    const target = e.target;
    if (target.closest('button') || target.closest('input')) return;
    const rel = e.target.closest('[data-invoice-wrap]');
    if (!rel) return;
    const innerBox = rel.getBoundingClientRect();
    const x = e.clientX - innerBox.left - 9;
    const y = e.clientY - innerBox.top - 9;
    const id = `n${Date.now()}`;
    setNotes(prev => [
      ...prev,
      {
        id, x, y, color: pickColor, internalOnly: false,
        body: '(empty note — type a reply below to add content)',
        preview: 'New note',
        author: ROLES[viewerRole].label, authorRole: viewerRole, time: 'just now',
        replies: [], mentions: [], resolved: false,
      },
    ]);
    setActiveId(id);
  };

  const role = ROLES[viewerRole];
  const RoleIcon = role.icon;

  return (
    <div className="min-h-screen bg-slate-50">
      <style>{`
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 0 0px rgba(0,0,0,0), 0 6px 14px -2px rgba(15,23,42,0.25); }
          50%      { box-shadow: 0 0 0 8px rgba(0,0,0,0.06), 0 6px 14px -2px rgba(15,23,42,0.25); }
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6">
          <Link to="/" className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 mb-3">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[11px] uppercase tracking-wide bg-purple-100 text-purple-800 px-2 py-1 rounded font-semibold">Mockup · final</span>
            <span className="text-[11px] uppercase tracking-wide bg-amber-100 text-amber-800 px-2 py-1 rounded font-semibold">Not wired yet</span>
            <span className="text-[11px] uppercase tracking-wide bg-emerald-100 text-emerald-800 px-2 py-1 rounded font-semibold">Role-aware</span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900">Annotate receipts — final preview</h1>
          <p className="text-slate-600 mt-2 max-w-3xl">
            Switch the "Viewing as" role below to see exactly what your team / banker / contractor sees on the same invoice.
            Internal-only notes (🔒 padlock badge) stay hidden from banker + contractor views.
          </p>
        </div>

        {/* Role switcher */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm mb-5">
          <div className="flex items-center gap-2 mb-3">
            <Eye className="w-4 h-4 text-slate-600" />
            <span className="text-sm font-semibold text-slate-900">Viewing as</span>
            <span className="text-xs text-slate-500">— click a role to see how the same invoice changes</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(ROLES).map(([k, r]) => {
              const Icon = r.icon;
              const active = viewerRole === k;
              return (
                <button
                  key={k}
                  onClick={() => { setViewerRole(k); setActiveId(null); }}
                  className={`text-left rounded-xl border p-3 transition ${active ? 'border-blue-600 bg-blue-50 ring-2 ring-blue-200' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="w-4 h-4 text-slate-700" />
                    <span className="text-sm font-semibold text-slate-900">{r.label}</span>
                  </div>
                  <div className="text-xs text-slate-600">{r.desc}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Active role banner */}
        <div className={`mb-5 p-4 rounded-2xl border ${role.tone} flex items-start gap-3`}>
          <RoleIcon className="w-5 h-5 mt-0.5" />
          <div>
            <div className="text-sm font-semibold">Now viewing as {role.label}</div>
            <div className="text-xs mt-1">
              {visibleNotes.length} of {notes.length} notes visible · {notes.length - visibleNotes.length > 0 && (
                <span className="font-semibold">{notes.length - visibleNotes.length} hidden (internal-only)</span>
              )}{notes.length - visibleNotes.length === 0 && <span>nothing hidden</span>}
            </div>
          </div>
        </div>

        {/* Context card */}
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-2xl p-4 text-sm text-blue-900 flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold mb-1">Scenario</div>
            <p className="leading-relaxed">
              This is Block 7's framing invoice ($35,800). Your team has added internal notes,
              your banker (investor role) has flagged a question, and the contractor has already
              replied to one thread. Internal-only notes are hidden from banker + contractor.
            </p>
          </div>
        </div>

        {/* Legend + color picker */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <Legend />
          <div className="bg-white border border-slate-200 rounded-xl p-2 shadow-sm inline-flex items-center gap-2 text-xs">
            <span className="text-slate-500 px-1">New note color:</span>
            {Object.entries(COLORS).map(([k, c]) => (
              <button
                key={k}
                onClick={() => setPickColor(k)}
                title={c.label}
                style={{
                  width: 22, height: 22, borderRadius: '50%', background: c.dot,
                  boxShadow: pickColor === k ? `0 0 0 3px ${c.halo}` : 'none',
                  border: pickColor === k ? '2px solid #fff' : '2px solid transparent',
                  outline: pickColor === k ? `2px solid ${c.dot}` : 'none',
                  cursor: 'pointer',
                }}
              />
            ))}
            <span className="text-slate-400 px-1 ml-1 inline-flex items-center gap-1">
              <Plus className="w-3 h-3" /> Click the doc to drop a dot
            </span>
          </div>
        </div>

        {/* Document */}
        <div className="relative" onClick={() => setActiveId(null)}>
          <div data-invoice-wrap style={{ position: 'relative', maxWidth: 780 }} onClick={handleAddDot}>
            <SampleInvoice>
              {visibleNotes.map(n => (
                <Dot key={n.id} note={n} active={n.id === activeId} onClick={() => setActiveId(n.id)} />
              ))}
              {active && (
                <StickyNote
                  note={active}
                  role={viewerRole}
                  onClose={() => setActiveId(null)}
                  onResolve={handleResolve}
                  onDelete={handleDelete}
                  onReply={handleReply}
                  onToggleInternal={handleToggleInternal}
                />
              )}
            </SampleInvoice>
          </div>
        </div>

        {/* Permissions reference */}
        <div className="mt-10 bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 mb-3">Permission matrix (final)</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="text-slate-500 uppercase tracking-wide text-[10px]">
                <tr>
                  <th className="py-2 pr-3">Role</th>
                  <th className="py-2 pr-3">Add notes</th>
                  <th className="py-2 pr-3">Reply</th>
                  <th className="py-2 pr-3">Resolve</th>
                  <th className="py-2 pr-3">Delete</th>
                  <th className="py-2 pr-3">Internal-only toggle</th>
                  <th className="py-2 pr-3">Sees internal notes</th>
                </tr>
              </thead>
              <tbody className="text-slate-700">
                <tr className="border-t border-slate-100"><td className="py-2 pr-3 font-semibold">Owner</td><td>✅ Any file</td><td>✅ Any</td><td>✅ Any</td><td>✅ Any</td><td>✅</td><td>✅</td></tr>
                <tr className="border-t border-slate-100"><td className="py-2 pr-3 font-semibold">Project Manager</td><td>✅ Project files</td><td>✅</td><td>✅ Own + project</td><td>Own only</td><td>—</td><td>✅</td></tr>
                <tr className="border-t border-slate-100"><td className="py-2 pr-3 font-semibold">Accountant / Finance</td><td>✅ Project files</td><td>✅</td><td>✅ Own + project</td><td>Own only</td><td>✅</td><td>✅</td></tr>
                <tr className="border-t border-slate-100"><td className="py-2 pr-3 font-semibold">Banker (Investor)</td><td>✅ Shared projects</td><td>✅</td><td>Own only</td><td>❌</td><td>❌</td><td>❌</td></tr>
                <tr className="border-t border-slate-100"><td className="py-2 pr-3 font-semibold">Contractor</td><td>✅ Own files</td><td>Own files / @mentions</td><td>Own only</td><td>Own only</td><td>❌</td><td>❌</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Locked summary */}
        <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-2xl p-5">
          <h3 className="text-base font-semibold text-emerald-900 mb-2">What ships when you approve</h3>
          <ul className="text-sm text-emerald-900 space-y-1 list-disc pl-5">
            <li>Color-coded dots (🔵🟡🟢🟣) + sticky-note popups on receipts &amp; lien waivers</li>
            <li>Internal-only padlock toggle (owner + accountant)</li>
            <li>Investor role upgraded to read + annotate (banker access via existing invite-by-email flow)</li>
            <li>Two-way thread with contractors on their own invoices (via existing contractor portal)</li>
            <li>Bell-notification feed (P1 from your backlog — bundled in)</li>
            <li>Email digest of unresolved threads (opt-out-able)</li>
            <li>Auto-flatten unresolved + non-internal notes onto the draw-packet PDF when shipping to the bank's finance dept</li>
          </ul>
          <div className="mt-4 text-sm text-emerald-900">
            <strong>Build order:</strong> Project Hub Phase 2 first → then annotation + bell notifications → ship in one combined deploy with the View Receipts popup fix that's already in preview.
          </div>
        </div>

        <div className="mt-10 text-center text-xs text-slate-400">
          Mockup only · nothing saved · reply &quot;approved&quot; in chat to start Project Hub Phase 2
        </div>
      </div>
    </div>
  );
}
